(function () {
  "use strict";

  const STORAGE_KEY = "theme:compare";
  const MAX_ITEMS = 3;
  const META_SECTION_ID = "compare-product-data";

  class CompareManager {
    constructor() {
      this.items = this.load();
      this.cache = new Map();
      this.root = document.getElementById("CompareRoot");
      this.moneyFormat =
        (this.root && this.root.dataset.moneyFormat) || "${{amount}}";
      this.strings = this._readStrings();
      this.fields = this._readFields();

      this._bindEvents();
      this._observeCards();
      this.sync();
    }

    load() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed)
          ? parsed.filter((i) => i && i.id && i.handle)
          : [];
      } catch (e) {
        return [];
      }
    }

    save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        /* storage full / blocked — ignore */
      }
      this.sync();
    }

    isCompared(id) {
      return this.items.some((i) => String(i.id) === String(id));
    }

    count() {
      return this.items.length;
    }

    add(id, handle) {
      if (!id || !handle) return;
      if (this.isCompared(id)) return;
      if (this.items.length >= MAX_ITEMS) {
        this.toast(this.strings.max);
        return;
      }
      this.items.push({ id: Number(id) || id, handle: handle });
      this.save();
    }

    remove(id) {
      this.items = this.items.filter((i) => String(i.id) !== String(id));
      this.save();
    }

    toggle(id, handle) {
      if (this.isCompared(id)) {
        this.remove(id);
      } else {
        this.add(id, handle);
      }
    }

    clear() {
      this.items = [];
      this.save();
    }

    sync() {
      this.updateButtons();
      this.updateCounter();
      if (this._drawerOpen()) this.renderDrawer();
      if (this._modalOpen()) this.renderCompareModal();
    }

    updateButtons() {
      document.querySelectorAll("[data-compare-toggle]").forEach((btn) => {
        const active = this.isCompared(btn.dataset.productId);
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
        const label = btn.querySelector("[data-compare-label]");
        if (label)
          label.textContent = active
            ? this.strings.added
            : this.strings.compare;
      });
    }

    updateCounter() {
      const n = this.count();
      const floating = document.getElementById("CompareFloating");
      if (!floating) return;

      floating.hidden = n === 0;
      floating.classList.toggle("is-ready", n >= 2);

      floating.querySelectorAll("[data-compare-count]").forEach((el) => {
        el.textContent = n;
      });
      document.querySelectorAll("[data-compare-drawer-count]").forEach((el) => {
        el.textContent = n;
      });
    }

    /** Fetch live product JSON from Shopify. Cached within a render pass. */
    async fetchProduct(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      const res = await fetch(
        `${window.Shopify ? window.Shopify.routes.root : "/"}products/${handle}.js`,
      );
      if (!res.ok) throw new Error(`Product fetch failed: ${handle}`);
      const data = await res.json();
      
      this.cache.set(handle, data);
      return data;
    }

    /** Fetch collections + dynamic metafields via the compare-data section. */
    async fetchMeta(handle) {
      try {
        const root = window.Shopify ? window.Shopify.routes.root : "/";
        const res = await fetch(
          `${root}products/${handle}?section_id=${META_SECTION_ID}`,
        );
        if (!res.ok) return null;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const node = doc.querySelector("[data-compare-data]");
        if (!node) return null;
        
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    _drawer() {
      return document.getElementById("CompareDrawer");
    }
    _drawerOpen() {
      const d = this._drawer();
      return d && d.classList.contains("is-open");
    }

    openDrawer() {
      const d = this._drawer();
      if (!d) return;
      d.classList.add("is-open");
      d.setAttribute("aria-hidden", "false");
      document.body.classList.add("compare-drawer-open");
      this.renderDrawer();
    }

    closeDrawer() {
      const d = this._drawer();
      if (!d) return;
      d.classList.remove("is-open");
      d.setAttribute("aria-hidden", "true");
      document.body.classList.remove("compare-drawer-open");
    }

    async renderDrawer() {
      const list = document.getElementById("CompareDrawerList");
      if (!list) return;

      if (!this.items.length) {
        this.closeDrawer();
        return;
      }

      this.cache.clear();
      list.setAttribute("aria-busy", "true");

      const products = await Promise.all(
        this.items.map((i) => this.fetchProduct(i.handle).catch(() => null)),
      );

      list.innerHTML = products
        .map((p, idx) => {
          if (!p) return "";
          const item = this.items[idx];
          const img = p.featured_image
            ? this._sizedImage(p.featured_image, 120)
            : "";
          return `
            <div class="compare-drawer__card" data-compare-card="${item.id}">
              <button type="button" class="compare-drawer__remove" data-compare-remove="${item.id}" aria-label="${this._esc(this.strings.remove)}">&times;</button>
              <div class="compare-drawer__media">${img ? `<img src="${img}" alt="${this._esc(p.title)}" loading="lazy" width="60" height="60">` : ""}</div>
              <div class="compare-drawer__info">
                <span class="compare-drawer__title">${this._esc(p.title)}</span>
                <span class="compare-drawer__price">${this._priceHtml(p)}</span>
              </div>
            </div>`;
        })
        .join("");

      list.scrollLeft = 0;
      list.setAttribute("aria-busy", "false");
    }

    _modal() {
      return document.getElementById("CompareModal");
    }
    _modalOpen() {
      const m = this._modal();
      return m && m.classList.contains("is-open");
    }

    openModal() {
      const m = this._modal();
      if (!m || !this.items.length) return;
      if (this.items.length < 2) {
        this.toast(this.strings.minProducts);
        return;
      }
      this._savedScrollY = window.scrollY;
      document.body.style.top = `-${this._savedScrollY}px`;
      document.body.classList.add("compare-modal-open");
      m.classList.add("is-open");
      m.setAttribute("aria-hidden", "false");
      this.renderCompareModal();
    }

    closeModal() {
      const m = this._modal();
      if (!m) return;
      m.classList.remove("is-open");
      m.setAttribute("aria-hidden", "true");
      const scrollY = this._savedScrollY || 0;
      document.body.classList.remove("compare-modal-open");
      document.body.style.top = "";
      window.scrollTo(0, scrollY);
    }

    async renderCompareModal() {
      const body = document.getElementById("CompareModalBody");
      if (!body) return;

      if (!this.items.length) {
        this.closeModal();
        return;
      }

      this.cache.clear();
      body.innerHTML = `<p class="compare-modal__loading">${this._esc(this.strings.loading)}</p>`;

      // Fetch live core data + metafields for each product in parallel.
      const data = await Promise.all(
        this.items.map(async (i) => {
          const [core, meta] = await Promise.all([
            this.fetchProduct(i.handle).catch(() => null),
            this.fetchMeta(i.handle),
          ]);
          return core ? { item: i, core, meta } : null;
        }),
      );

      const products = data.filter(Boolean);
      if (!products.length) {
        body.innerHTML = `<p class="compare-modal__loading">${this._esc(this.strings.empty)}</p>`;
        return;
      }

      body.innerHTML = this._buildTable(products);

      if (window.ratingSnippet) {
        window.ratingSnippet("ruk_rating_snippet", {
          store: "markslojd.com",
          color: "#637e60",
          linebreak: true,
          usePolaris: true,
          text: "Reviews",
        });
      }

      setTimeout(() => {
        let anyReviewed = false;
        body.querySelectorAll(".compare-rating").forEach((wrapper) => {
          const snippet = wrapper.querySelector(".ruk_rating_snippet");
          const empty = wrapper.querySelector(".compare-rating__empty");
          if (!empty) return;
          if (snippet && snippet.children.length > 0) {
            const t = snippet.textContent.trim();
            // Reviews.io renders "0 Reviews" or starts with "0" for unreviewed products
            if (!t || /^0/.test(t)) {
              snippet.style.display = "none";
              empty.innerHTML = "&mdash;";
              empty.style.display = "block";
            } else {
              empty.style.display = "none";
              anyReviewed = true;
            }
          } else {
            // Widget didn't render at all — show dash, not fake stars
            empty.innerHTML = "&mdash;";
            empty.style.display = "block";
          }
        });

        // Drop the whole row when none of the compared products has reviews
        const reviewRow = body.querySelector(".compare-table__row--reviews");
        if (reviewRow) reviewRow.style.display = anyReviewed ? "" : "none";
      }, 500);
    }

    _buildTable(products) {
      const rows = [];
      rows.push(
        this._row(
          "",
          products
            .map((p) => {
              const img = p.core.featured_image
                ? this._sizedImage(p.core.featured_image, 300)
                : "";
              const vid =
                (p.meta && p.meta.variant_id) ||
                (p.core.variants &&
                  p.core.variants[0] &&
                  p.core.variants[0].id);
              const productUrl = this._esc(
                p.core.url || (p.meta && p.meta.url) || "#",
              );
              return `
                <th scope="col" class="compare-table__head">
                  <div class="compare-table__head-inner">
                    <wishlist-button-collection data-product-handle="${this._esc(p.core.handle || "")}" show-icon></wishlist-button-collection>
                  
                    <div class="compare-table__img-wrap">
                      <a href="${productUrl}" class="compare-table__img">
                        ${img ? `<img src="${img}" alt="${this._esc(p.core.title)}" loading="lazy">` : ""}
                      </a>
                    
                    </div>
                    <span class="compare-table__title">${this._esc(p.core.title)}</span>
                    ${this._ctaHtml(p, vid, productUrl)}
                  </div>
                </th>`;
            })
            .join(""),
          true,
        ),
      );

      // Returns "" when the product has no value — callers use that to decide
      // between a dash and dropping the row altogether.
      const getMetafield = (product, namespace, key) => {
        if (!product.meta || !product.meta.metafields) return "";
        const field = product.meta.metafields.find(
          (mf) =>
            mf.namespace === namespace &&
            mf.key.toLowerCase() === key.toLowerCase(),
        );
        if (!field) return "";
        const value = field.value;
        if (Array.isArray(value)) {
          const joined = value
            .filter((v) => v != null && String(v).trim() !== "")
            .join(", ");
          return joined ? this._esc(joined) : "";
        }
        if (value && typeof value === "object") {
          const label =
            value.label || value.title || value.name || value.value || "";
          return String(label).trim() ? this._esc(String(label)) : "";
        }
        return value != null && String(value).trim() !== ""
          ? this._esc(String(value))
          : "";
      };

      // Product review row
      rows.push(
        this._dataRow(
          "Product Review",
          products.map(
            (p) => `
            <div class="compare-rating">
              <div class="ruk_rating_snippet"
                data-sku="${this._esc(p.core.handle)};${p.core.variants.map((v) => v.sku).join(";")};${p.core.variants.map((v) => v.id).join(";")}">
              </div>
              <div class="compare-rating__empty">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
            </div>
          `,
          ),
          "compare-table__row--reviews",
        ),
      );

      // Price row
      rows.push(
        this._dataRow(
          this.strings.price,
          products.map((p) => this._priceHtml(p.core)),
        ),
      );

      // Metafield rows — order and labels come from the #CompareRows JSON
      // rendered by snippets/compare-drawer.liquid, so they stay translated.
      this.fields.forEach(({ label, namespace, key }) => {
        const values = products.map((p) => getMetafield(p, namespace, key));
        // Skip the row entirely when none of the compared products has a value
        if (!values.some((v) => v !== "")) return;
        rows.push(
          this._dataRow(
            this._esc(label),
            values.map((v) => v || "—"),
          ),
        );
      });

      return `
        <div class="compare-table__scroll">
          <table class="compare-table">
            <tbody>${rows.join("")}</tbody>
          </table>
        </div>`;
    }

    _row(label, cellsHtml, isHead, rowClass) {
      const labelCell = isHead
        ? '<td class="compare-table__corner"></td>'
        : `<th scope="row" class="compare-table__label">${label}</th>`;
      const cls = [isHead ? "compare-table__row--head" : "", rowClass || ""]
        .filter(Boolean)
        .join(" ");
      return `<tr${cls ? ` class="${cls}"` : ""}>${labelCell}${cellsHtml}</tr>`;
    }

    _dataRow(label, values, rowClass) {
      const cells = values
        .map((v) => `<td class="compare-table__cell">${v}</td>`)
        .join("");
      return this._row(label, cells, false, rowClass);
    }

    async addToCart(variantId, button) {
      if (!variantId) return;
      const cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      if (button) {
        button.classList.add("loading");
        button.setAttribute("aria-disabled", "true");
      }

      const body = {
        items: [{ id: Number(variantId), quantity: 1 }],
      };
      if (cart && typeof cart.getSectionsToRender === "function") {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      try {
        const res = await fetch(`${window.routes.cart_add_url}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
          },
          body: JSON.stringify(body),
        });
        const response = await res.json();

        if (response.status) {
          this.toast(
            response.description || response.message || this.strings.cartError,
          );
          return;
        }

        if (typeof publish === "function" && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "compare",
            cartData: response,
          });
        }

        // Refresh + reveal the theme's existing cart UI.
        if (cart && typeof cart.renderContents === "function") {
          this.closeModal();
          cart.renderContents(response);
          if (cart.classList.contains("is-empty"))
            cart.classList.remove("is-empty");
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (e) {
        this.toast(this.strings.cartError);
      } finally {
        if (button) {
          button.classList.remove("loading");
          button.removeAttribute("aria-disabled");
        }
      }
    }

    toast(message) {
      let el = document.getElementById("CompareToast");
      if (!el) return;
      el.textContent = message;
      el.classList.add("is-visible");
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(
        () => el.classList.remove("is-visible"),
        3000,
      );
    }

    /**
     * Comparison rows defined in snippets/compare-drawer.liquid: an ordered
     * list of { key, label, namespace? }. Namespace defaults to "gung".
     */
    _readFields() {
      const el = document.getElementById("CompareRows");
      if (!el) return [];
      try {
        const parsed = JSON.parse(el.textContent);
        if (!Array.isArray(parsed)) return [];
        return parsed
          .filter((f) => f && f.key)
          .map((f) => ({
            key: f.key,
            namespace: f.namespace || "gung",
            label: f.label || f.key,
          }));
      } catch (e) {
        return [];
      }
    }

    _readStrings() {
      const d = (this.root && this.root.dataset) || {};
      
      return {
        compare: d.strCompare || "Compare",
        added: d.strAdded || "Added",
        remove: d.strRemove || "Remove",
        max: d.strMax || "Maximum 3 products can be compared.",
        minProducts: d.strMinProducts || "Add at least 2 products to compare.",
        loading: d.strLoading || "Loading…",
        empty: d.strEmpty || "No products to compare.",
        price: d.strPrice || "Price",
        type: d.strType || "Type",
        collections: d.strCollections || "Collection",
        availability: d.strAvailability || "Availability",
        soldOut: d.strSoldOut || "Sold out",
        soldOutButton: d.strSoldOutButton || "Sold out",
        notifyMe: d.strNotifyMe || "Notify Me",
        addToCart: d.strAddToCart || "Add to cart",
        cartError: d.strCartError || "Could not add to cart.",
      };
    }

    _ctaHtml(p, vid, productUrl) {
      const meta = p.meta || {};
      const inStock =
        typeof meta.in_stock === "boolean" ? meta.in_stock : !!p.core.available;

      if (inStock) {
        return `<button type="button" class="compare-table__atc button button--primary"
                  data-compare-add-to-cart="${vid || ""}" ${vid ? "" : "disabled"}>
                  ${this._esc(this.strings.addToCart)}
                </button>`;
      }

      const restockDate = String(meta.next_available_date || "").trim();
      if (!restockDate) {
        return `<button type="button" class="compare-table__atc button button--primary" disabled>
                  ${this._esc(this.strings.soldOutButton)}
                </button>`;
      }

      return `<a href="${this._esc(productUrl)}"
                class="compare-table__atc compare-table__atc--notify button button--primary ">
                ${this._esc(this.strings.notifyMe)}
              </a>`;
    }

    _priceHtml(p) {
      const price = this._money(p.price);
      const onSale = p.compare_at_price && p.compare_at_price > p.price;
      if (!onSale) {
        return `<span class="compare-price compare-price--sale">${price}</span>`;
      }
      return `<span class="compare-price compare-price--sale custom-price">${price}</span>
                <s class="compare-price compare-price--was">${this._money(p.compare_at_price)}</s>`;
    }

    _sizedImage(url, size) {
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set("width", size);
        return u.href;
      } catch (e) {
        return url;
      }
    }

    _money(cents) {
      if (cents == null || cents === "") return "";
      if (typeof cents === "string") cents = cents.replace(/[^0-9.-]/g, "");

      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const format = this.moneyFormat;

      function withDelimiters(number, precision, thousands, decimal) {
        precision = precision == null ? 2 : precision;
        thousands = thousands == null ? "," : thousands;
        decimal = decimal == null ? "." : decimal;
        if (isNaN(number) || number == null) return "0";
        number = (number / 100).toFixed(precision);
        const parts = number.split(".");
        const dollars = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + thousands,
        );
        const centsPart = parts[1] ? decimal + parts[1] : "";
        return dollars + centsPart;
      }

      const match = format.match(placeholderRegex);
      const token = match ? match[1] : "amount";
      let value;
      switch (token) {
        case "amount":
          value = withDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = withDelimiters(cents, 0);
          break;
        case "amount_with_comma_separator":
          value = withDelimiters(cents, 2, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = withDelimiters(cents, 0, ".", "");
          break;
        case "amount_with_apostrophe_separator":
          value = withDelimiters(cents, 2, "'", ".");
          break;
        case "amount_with_space_separator":
          value = withDelimiters(cents, 2, " ", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = withDelimiters(cents, 0, " ", "");
          break;
        default:
          value = withDelimiters(cents, 2);
      }

      return format.replace(placeholderRegex, value);
    }

    _esc(str) {
      if (str == null) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    _bindEvents() {
      // Delegated clicks — works for cards injected after page load (AJAX facets).
      document.addEventListener("click", (e) => {
        const toggle = e.target.closest("[data-compare-toggle]");
        if (toggle) {
          e.preventDefault();
          this.toggle(toggle.dataset.productId, toggle.dataset.productHandle);
          toggle.blur();
          return;
        }

        if (e.target.closest("[data-compare-open]")) {
          e.preventDefault();
          this.openDrawer();
          return;
        }
        if (e.target.closest("[data-compare-close]")) {
          this.closeDrawer();
          return;
        }
        if (e.target.closest("[data-compare-open-modal]")) {
          this.openModal();
          return;
        }
        if (e.target.closest("[data-compare-modal-close]")) {
          this.closeModal();
          return;
        }
        if (e.target.closest("[data-compare-clear]")) {
          this.clear();
          return;
        }

        const remove = e.target.closest("[data-compare-remove]");
        if (remove) {
          this.remove(remove.dataset.compareRemove);
          return;
        }

        const atc = e.target.closest("[data-compare-add-to-cart]");
        if (atc) {
          this.addToCart(atc.dataset.compareAddToCart, atc);
          return;
        }
      });

      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        if (this._modalOpen()) this.closeModal();
        else if (this._drawerOpen()) this.closeDrawer();
      });

      window.addEventListener("storage", (e) => {
        if (e.key === STORAGE_KEY) {
          this.items = this.load();
          this.sync();
        }
      });
    }

    _observeCards() {
      if (!("MutationObserver" in window)) return;
      let scheduled = false;
      const observer = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          this.updateButtons();
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function init() {
    window.CompareManager = new CompareManager();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
